// src/pages/ScoreCommentEdit.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Editor } from "@tinymce/tinymce-react";

import useUserLanguage from "../../hooks/useUserLanguage";
import useTranslations from "../../hooks/useTranslations";

export default function ScoreCommentEdit() {
  const { testId, categoryId, id } = useParams();
  const nav = useNavigate();
  const [testName, setTestName] = useState("");
  const [categoryName, setCategoryName] = useState("");

  const token = localStorage.getItem("admin_token");
  const base  = process.env.REACT_APP_API_BASE_URL;

  // ⬇️ EKLE: isim seçici + dizi normalize
  const pickName = (obj = {}) =>
    obj?.name ?? obj?.title ?? obj?.category_name ?? obj?.label ?? "";

  const normalizeArray = (data) =>
    Array.isArray(data) ? data
    : Array.isArray(data?.data) ? data.data
    : Array.isArray(data?.rows) ? data.rows
    : [];

  /* i18n */
  const lang = useUserLanguage();
  const { translations } = useTranslations(lang);
  const t = (k, d = "") => translations[k] || d;

  /* form state */
  const [form, setForm] = useState({
    min_score: "",
    max_score: "",
    summary: "",
    details: ""
  });
  const [loading, setLoading] = useState(false);

  // aralık limitleri
  const [lowFloor, setLowFloor] = useState(0);
  const [highCeil, setHighCeil] = useState(null);

  // test üst sınırı
  const [testMaxScore, setTestMaxScore] = useState(null);

  /* TinyMCE config */
  const tinymceConfig = {
    promotion: false,
    menubar: true,
    plugins: [
      "advlist autolink lists link image charmap preview anchor",
      "searchreplace visualblocks code fullscreen",
      "insertdatetime media table emoticons help wordcount"
    ].join(" "),
    toolbar:
      "undo redo | formatselect | bold italic underline strikethrough | " +
      "alignleft aligncenter alignright alignjustify | " +
      "bullist numlist outdent indent | link image media table | code help",
    automatic_uploads: false,
    images_upload_handler: (blobInfo, success, failure) => {
      const fd = new FormData();
      fd.append("file", blobInfo.blob(), blobInfo.filename());
      fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/score-comments/upload-image`,
        { method: "POST", body: fd }
      )
        .then(r => r.json())
        .then(json => {
          const url = json.location;
          const img = new Image();
          img.onload = () => {
            const html = `<img 
              src="${url}"
              width="${img.naturalWidth}"
              height="${img.naturalHeight}"
              style="max-width:100%;height:auto;"
            />`;
            window.tinymce.activeEditor.insertContent(html);
            success(url);
          };
          img.onerror = () => failure("Boyut bilgisi okunamadı");
          img.src = url;
        })
        .catch(err => failure("Yükleme hatası: " + err.message));
    },
    images_upload_tab: true,
    image_advtab: true,
    file_picker_types: "image",
    relative_urls: false,
    remove_script_host: false,
    convert_urls: true
  };

  useEffect(() => {
  (async () => {
    try {
      // Test adı
      try {
        const { data: test } = await axios.get(`${base}/api/tests/${testId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTestName(pickName(test) || `Test #${testId}`);
      } catch {
        setTestName(`Test #${testId}`);
      }

      // Kategori yoksa çık
      if (!categoryId) {
        setCategoryName("");
        return;
      }

      let name = "";

      // 1) /api/categories/:id
      try {
        const { data } = await axios.get(`${base}/api/categories/${categoryId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        name = pickName(data) || name;
      } catch {}

      // 2) /api/test-categories/:id
      if (!name) {
        try {
          const { data } = await axios.get(`${base}/api/test-categories/${categoryId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          name = pickName(data) || name;
        } catch {}
      }

      // 3) /api/tests/:testId/categories içinde ara
      if (!name) {
        try {
          const { data } = await axios.get(`${base}/api/tests/${testId}/categories`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const list = normalizeArray(data);
          const found = list.find(
            (c) =>
              String(c.id) === String(categoryId) ||
              String(c.category_id) === String(categoryId)
          );
          name = pickName(found) || name;
        } catch {}
      }

      setCategoryName(name || `Kategori #${categoryId}`);
    } catch {
      // sessiz geç
    }
  })();
}, [testId, categoryId, base, token]);


  /* load existing comment + compute floors + test max */
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("admin_token");

        // 1) bu yorumu getir
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/score-comments/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setForm({
          min_score: String(data.min_score),
          max_score: String(data.max_score),
          summary: data.summary || "",
          details: data.details || ""
        });

        // 2) tüm yorumları al
        const { data: list } = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/score-comments/tests/${data.test_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Kapsam: kategori bazlıysa sadece o kategori; değilse tüm test
        const inScope = categoryId
          ? list.filter(r => String(r.category_id ?? "") === String(categoryId))
          : list;

        // min_score’a göre sırala ve mevcut kaydın index’ini bul
        const sorted = [...inScope].sort((a, b) => a.min_score - b.min_score);
        const idx = sorted.findIndex(r => r.id === data.id);

        // 3) test min/max
        const { data: test } = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/tests/${data.test_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const testMin = Number.isFinite(test.min_score) ? test.min_score : 0;
        const testMax = test.max_score ?? test.total_score ?? null;
        setTestMaxScore(testMax);

        // komşular
        const prevMax = idx > 0 ? sorted[idx - 1].max_score : null;
        const nextMin = idx < sorted.length - 1 ? sorted[idx + 1].min_score : null;

        // taban/tavan (Infinity kullanma)
        const floor = prevMax == null ? testMin : prevMax + 1;
        const ceil = nextMin == null ? testMax : nextMin - 1;

        setLowFloor(floor);
        setHighCeil(ceil);
      } catch (err) {
        console.error("❌ Yükleme hatası:", err);
      }
    })();
  }, [id, categoryId]);

  const handle = e => {
    let { name, value } = e.target;
    if (name === "max_score" && testMaxScore != null) {
      const num = Number(value);
      if (!isNaN(num) && num > testMaxScore) value = String(testMaxScore);
    }
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const save = async e => {
    e.preventDefault();
    const min = +form.min_score;
    const max = +form.max_score;

    if (isNaN(min) || isNaN(max))
      return toast.error(t("ERR_REQ", "Puanlar sayısal olmalı"));
    if (max < min)
      return toast.error(t("ERR_RANGE", "Maksimum puan minimumdan küçük olamaz"));
    if (testMaxScore != null && max > testMaxScore)
      return toast.error(t("ERR_MAX_EXCEED", "Maksimum puan testin izin verdiği puanı geçemez"));
    if (min < lowFloor)
      return toast.error(t("ERR_MIN_FLOOR", `Min. puan en az ${lowFloor} olmalı`));
    if (highCeil != null && max > highCeil)
      return toast.error(t("ERR_MAX_CEIL", `Max. puan en fazla ${highCeil} olmalı`));

    setLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/score-comments/${id}`,
        {
          test_id: testId,
          category_id: categoryId ? Number(categoryId) : null,
          min_score: min,
          max_score: max,
          summary: form.summary.trim(),
          details: form.details.trim()
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } }
      );
     toast.success("Puan açıklaması başarıyla güncellendi!");

      setTimeout(() => {
        const returnUrl = categoryId
          ? `/test/${testId}/category/${categoryId}/score/table`
          : `/test/score/table/${testId}`;
        nav(returnUrl);
      }, 1000);
    } catch (err) {
      console.error("❌ Güncelleme hatası:", err);
      toast.error(t("UPDATE_FAILED", "Güncelleme sırasında hata oluştu"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
       <ToastContainer />
        {/* ▸ Context line */}
    <div className="d-flex align-items-center justify-content-between mb-3">
      <div className="small text-muted">
        {categoryId ? (
          <>
            Test: <strong>{testName }</strong>
            {" · "}
            Kategori: <strong>{categoryName}</strong>
          </>
        ) : (
          <>
            Test: <strong>{testName }</strong>
          </>
        )}
      </div>
    </div>
      <form className="card p-4" onSubmit={save}>
        <h5 className="mb-3 fw-bold">{t("EDIT_SCORE_COMMENT", "Puan Açıklamasını Düzenle")}</h5>

        {/* Puan Aralığı */}
        <div className="mb-3">
          <label className="form-label">Puan Aralığı</label>
          <div className="d-flex align-items-center gap-2">
            <input
              name="min_score"
              type="number"
              className="form-control"
              style={{ maxWidth: 120 }}
              value={form.min_score}
              min={lowFloor}
              max={highCeil ?? testMaxScore ?? undefined}
              step={1}
              onChange={handle}
              disabled={loading}
              required
            />
            <span className="fw-bold">-</span>
            <input
              name="max_score"
              type="number"
              className="form-control"
              style={{ maxWidth: 120 }}
              value={form.max_score}
              min={form.min_score}
              max={highCeil ?? testMaxScore ?? undefined}
              step={1}
              onChange={handle}
              disabled={loading}
              required
            />
          </div>
          <small className="text-muted">
            İzin verilen aralık: {lowFloor} – {highCeil ?? testMaxScore}
          </small>
        </div>

        {/* Özet */}
        <div className="mb-3">
          <label className="form-label fw-bold">{t("TABLE_SUMMARY", "Özet")}</label>
          <Editor
            tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.6.1/tinymce.min.js"
            value={form.summary}
            onEditorChange={content =>
              setForm(prev => ({ ...prev, summary: content }))
            }
            init={{ ...tinymceConfig, height: 400 }}
          />
        </div>

        {/* Detay */}
        <div className="mb-3">
          <label className="form-label fw-bold">{t("DETAILS", "Detay")}</label>
          <Editor
            tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.6.1/tinymce.min.js"
            value={form.details}
            onEditorChange={content =>
              setForm(prev => ({ ...prev, details: content }))
            }
            init={{ ...tinymceConfig, height: 600 }}
          />
        </div>

        {/* Kaydet */}
        <div className="d-flex justify-content-center">
          <button className="btn btn-primary" disabled={loading}>
            {loading ? t("LOADING", "Kaydediliyor…") : t("SAVE", "Kaydet")}
          </button>
        </div>
      </form>
    </div>
  );
}
